import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, Square, CalendarDays, Coffee, PlayCircle } from "lucide-react";
import { api, apiError, fmtHours, fmtTime, fmtDate, MONTHS, kindLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const now = new Date();

const openBreak = (shift) => (shift?.breaks || []).find((b) => b.start && !b.end) || null;
const totalBreakMs = (shift) =>
  (shift?.breaks || []).reduce((acc, b) => {
    if (!b.start) return acc;
    const end = b.end ? new Date(b.end).getTime() : Date.now();
    return acc + (end - new Date(b.start).getTime());
  }, 0);

export default function Dashboard() {
  const { user, pharmacy, net } = useAuth();
  const [shift, setShift] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const year = now.getFullYear();

  const load = useCallback(async () => {
    try {
      const [c, r, s] = await Promise.all([
        api.get("/attendance/current"),
        api.get("/attendance/records", { params: { year, month } }),
        api.get("/attendance/summary", { params: { year, month } }),
      ]);
      setShift(c.data.shift);
      setRecords(r.data);
      setSummary(s.data.rows.find((x) => x.operator_id === user.id) || s.data.rows[0] || null);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [year, month, user.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!shift) { setElapsed(0); return; }
    const tick = () => {
      const gross = Math.floor((Date.now() - new Date(shift.clock_in).getTime()) / 1000);
      const breakSec = Math.floor(totalBreakMs(shift) / 1000);
      setElapsed(Math.max(0, gross - breakSec));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [shift]);

  const action = async (endpoint, successMsg) => {
    setBusy(true);
    try {
      await api.post(`/attendance/${endpoint}`);
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const hh = String(Math.floor(elapsed / 3600)).padStart(2, "0");
  const mm = String(Math.floor((elapsed % 3600) / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const types = pharmacy?.absence_types || [];
  const onBreak = openBreak(shift);
  const canClock = net.ip_allowed || net.emergency;
  const breakMinutes = Math.floor(totalBreakMs(shift) / 60000);

  return (
    <div className="space-y-6" data-testid="operator-dashboard">
      <div>
        <p className="text-sm text-gray-600">Ciao {user.name}</p>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold">Timbratura</h1>
      </div>

      {!canClock && (
        <p data-testid="offnet-warning" className="rounded-lg bg-red-500/10 text-red-700 px-4 py-3 text-sm font-medium">
          Sei fuori dalla rete della farmacia (IP {net.ip}): ingresso, uscita e pausa sono disabilitati.
          Puoi comunque inviare richieste di ferie, permessi e malattia dalla pagina{" "}
          <Link to="/assenze" data-testid="offnet-absences-link" className="underline font-semibold">Assenze</Link>.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-8">
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${shift ? "bg-emerald-500 animate-pulse" : "bg-gray-300"}`} />
            <span data-testid="shift-status" className="text-sm font-semibold text-gray-600">
              {shift ? "In turno" : "Fuori turno"}
            </span>
          </div>
          <p data-testid="live-timer" className={`font-mono-num text-5xl sm:text-6xl font-bold mt-4 ${onBreak ? "text-amber-600" : ""}`}>
            {hh}:{mm}:{ss}
          </p>
          <p className="text-sm text-gray-600 mt-2">
            {shift ? `Ingresso alle ${fmtTime(shift.clock_in)}` : "Nessun turno aperto"}
            {shift && breakMinutes > 0 && (
              <span data-testid="break-total" className="ml-2 text-amber-700">· pausa {breakMinutes}m</span>
            )}
          </p>
          {onBreak && (
            <p data-testid="on-break-banner" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-100 text-amber-800 px-3 py-1.5 text-xs font-semibold">
              <Coffee className="h-3.5 w-3.5" /> In pausa da {fmtTime(onBreak.start)}
            </p>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              data-testid="clock-in-btn"
              disabled={busy || !!shift || !canClock}
              onClick={() => action("clock-in", "Ingresso registrato")}
              className="px-6 py-3 rounded-lg bg-emerald-600 text-white font-semibold flex items-center gap-2 transition-colors hover:bg-emerald-700 active:scale-95 disabled:opacity-40"
            >
              <Play className="h-4 w-4" /> Ingresso
            </button>
            {shift && !onBreak && (
              <button
                data-testid="break-start-btn"
                disabled={busy || !canClock}
                onClick={() => action("break-start", "Pausa iniziata")}
                className="px-5 py-3 rounded-lg bg-amber-500 text-white font-semibold flex items-center gap-2 transition-colors hover:bg-amber-600 active:scale-95 disabled:opacity-40"
              >
                <Coffee className="h-4 w-4" /> Inizia pausa
              </button>
            )}
            {shift && onBreak && (
              <button
                data-testid="break-end-btn"
                disabled={busy || !canClock}
                onClick={() => action("break-end", "Pausa terminata")}
                className="px-5 py-3 rounded-lg bg-amber-600 text-white font-semibold flex items-center gap-2 transition-colors hover:bg-amber-700 active:scale-95 disabled:opacity-40"
              >
                <PlayCircle className="h-4 w-4" /> Termina pausa
              </button>
            )}
            <button
              data-testid="clock-out-btn"
              disabled={busy || !shift || !canClock}
              onClick={() => action("clock-out", "Uscita registrata")}
              className="px-6 py-3 rounded-lg bg-red-500 text-white font-semibold flex items-center gap-2 transition-colors hover:bg-red-600 active:scale-95 disabled:opacity-40"
            >
              <Square className="h-4 w-4" /> Uscita
            </button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">Riepilogo mensile</h2>
            <CalendarDays className="h-4 w-4 text-gray-400" />
          </div>
          <select
            data-testid="month-select"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="mt-3 w-full h-10 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          >
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m} {year}</option>)}
          </select>
          <div className="mt-5 space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-600">Ore lavorate</p>
              <p data-testid="summary-hours" className="font-mono-num text-3xl font-bold">
                {fmtHours(summary?.minutes || 0)}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Giorni</p>
                <p data-testid="summary-days" className="font-mono-num font-bold text-lg">{summary?.days || 0}</p>
              </div>
              {types.map((t) => (
                <div key={t.key}>
                  <p className="text-gray-600">{t.label}</p>
                  <p data-testid={`summary-${t.key}`} className="font-mono-num font-bold text-lg">
                    {summary?.absences?.[t.key] || 0}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-testid="records-table">
        <h2 className="text-base font-semibold">Timbrature del mese</h2>
        {records.length === 0 ? (
          <p className="text-sm text-gray-600 mt-4">Nessuna timbratura registrata.</p>
        ) : (
          <table className="w-full mt-4 text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-600">
                <th className="pb-3">Giorno</th><th className="pb-3">Ingresso</th><th className="pb-3">Uscita</th><th className="pb-3 text-right">Ore</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} data-testid={`record-row-${r.id}`} className="border-t border-gray-100">
                  <td className="py-3">{fmtDate(r.day)}</td>
                  <td className="py-3 font-mono-num">{fmtTime(r.clock_in)}</td>
                  <td className="py-3 font-mono-num">{r.clock_out ? fmtTime(r.clock_out) : "in corso"}</td>
                  <td className="py-3 font-mono-num text-right">{r.minutes != null ? fmtHours(r.minutes) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="sr-only">{kindLabel(pharmacy, "ferie")}</p>
    </div>
  );
}
